import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Skeleton,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DataGrid, itIT } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Receipt as ReceiptIcon,
  Description as DescriptionIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { customersApi } from '../../services/api';
import MobileCardList from '../../components/MobileCardList';
import ResponsiveFilters from '../../components/ResponsiveFilters';
import ResponsiveHeader from '../../components/ResponsiveHeader';

const CustomerList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [filterType, setFilterType] = useState('all');
  const [filterActive, setFilterActive] = useState('all');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Query clienti
  const { data: rawData, isLoading, error } = useQuery({
    queryKey: ['customers', { page, pageSize, search: searchQuery, type: filterType, active: filterActive }],
    queryFn: () => customersApi.getAll({
      page: page + 1,
      limit: pageSize,
      search: searchQuery,
      customer_type: filterType !== 'all' ? filterType : undefined,
      is_active: filterActive !== 'all' ? filterActive === 'active' : undefined,
    }).then(res => ({
      customers: (res.data?.data || res.data?.customers || []).map(c => ({
        ...c,
        name: c.business_name || `${c.last_name || ''} ${c.first_name || ''}`.trim(),
      })),
      total: res.data?.pagination?.total || res.data?.total || 0,
      pagination: res.data?.pagination
    })),
    placeholderData: (prev) => prev
  });
  const data = rawData;

  // Mutation elimina
  const deleteMutation = useMutation({
    mutationFn: (id) => customersApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setDeleteDialogOpen(false);
      setSelectedCustomer(null);
    },
  });

  // Handlers
  const handleMenuOpen = (event, customer) => {
    setMenuAnchor(event.currentTarget);
    setSelectedCustomer(customer);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleView = () => {
    handleMenuClose();
    navigate(`/crm/clienti/${selectedCustomer.id}`);
  };

  const handleEdit = () => {
    handleMenuClose();
    navigate(`/crm/clienti/${selectedCustomer.id}/modifica`);
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    deleteMutation.mutate(selectedCustomer.id);
  };

  const handleViewInvoices = () => {
    handleMenuClose();
    navigate(`/crm/fatture?customer=${selectedCustomer.id}`);
  };

  const handleViewContracts = () => {
    handleMenuClose();
    navigate(`/crm/contratti?customer=${selectedCustomer.id}`);
  };

  // Colonne DataGrid
  const columns = [
    {
      field: 'legacy_code',
      headerName: 'Codice',
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'name',
      headerName: 'Nome / Ragione Sociale',
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => 
        params.row.business_name || `${params.row.last_name} ${params.row.first_name}`,
    },
    {
      field: 'customer_type',
      headerName: 'Tipo',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value === 'business' ? 'Azienda' : 'Privato'}
          size="small"
          color={params.value === 'business' ? 'primary' : 'default'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'city',
      headerName: 'Località',
      width: 150,
    },
    {
      field: 'phone',
      headerName: 'Telefono',
      width: 130,
    },
    {
      field: 'fiscal_code',
      headerName: 'Cod. Fiscale / P.IVA',
      width: 180,
      valueGetter: (params) => params.row.vat_number || params.row.fiscal_code,
    },
    {
      field: 'azienda',
      headerName: 'Azienda',
      width: 150,
      valueGetter: (params) => params.row.azienda?.company_name || '-',
    },
    {
      field: 'is_active',
      headerName: 'Stato',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Attivo' : 'Inattivo'}
          size="small"
          color={params.value ? 'success' : 'default'}
        />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => handleMenuOpen(e, params.row)}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      {/* Header */}
      <ResponsiveHeader
        title="Clienti"
        subtitle="Gestione anagrafica clienti"
        primaryAction={{
          label: isMobile ? 'Nuovo' : 'Nuovo Cliente',
          icon: <AddIcon />,
          onClick: () => navigate('/crm/clienti/nuovo'),
        }}
      />

      {/* Filtri e Ricerca */}
      <Card sx={{ mb: isMobile ? 1.5 : 3 }}>
        <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
          <ResponsiveFilters
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Cerca per nome, codice, P.IVA..."
            activeFilterCount={(filterType !== 'all' ? 1 : 0) + (filterActive !== 'all' ? 1 : 0)}
            onClearFilters={() => { setFilterType('all'); setFilterActive('all'); }}
          >
            <FormControl sx={{ minWidth: { xs: '100%', sm: 150 } }} size="small">
              <InputLabel>Tipo</InputLabel>
              <Select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                label="Tipo"
              >
                <MenuItem value="all">Tutti</MenuItem>
                <MenuItem value="private">Privati</MenuItem>
                <MenuItem value="business">Aziende</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl sx={{ minWidth: { xs: '100%', sm: 150 } }} size="small">
              <InputLabel>Stato</InputLabel>
              <Select
                value={filterActive}
                onChange={(e) => setFilterActive(e.target.value)}
                label="Stato"
              >
                <MenuItem value="all">Tutti</MenuItem>
                <MenuItem value="active">Attivi</MenuItem>
                <MenuItem value="inactive">Inattivi</MenuItem>
              </Select>
            </FormControl>
          </ResponsiveFilters>
        </CardContent>
      </Card>

      {/* Tabella / Card List */}
      <Card>
        <CardContent sx={{ p: isMobile ? 0 : 0 }}>
          {error ? (
            <Alert severity="error" sx={{ m: 2 }}>
              Errore nel caricamento dei clienti
            </Alert>
          ) : isMobile ? (
            <MobileCardList
              rows={data?.customers || []}
              loading={isLoading}
              onRowClick={(row) => navigate(`/crm/clienti/${row.id}`)}
              primaryField="name"
              secondaryField="legacy_code"
              statusField={{
                field: 'is_active',
                labelMap: { true: 'Attivo', false: 'Inattivo', 1: 'Attivo', 0: 'Inattivo' },
                colorMap: { true: 'success', false: 'default', 1: 'success', 0: 'default' },
              }}
              extraFields={[
                { field: 'city', label: 'Località' },
                { field: 'phone', label: 'Tel' },
              ]}
              chipField={{
                field: 'customer_type',
                labelMap: { business: 'Azienda', private: 'Privato' },
              }}
              pagination={{
                page,
                pageSize,
                total: data?.total || 0,
                onPageChange: (newPage) => setPage(newPage),
              }}
              idField="id"
              emptyMessage="Nessun cliente trovato"
            />
          ) : (
            <DataGrid
              rows={data?.customers || []}
              columns={columns}
              loading={isLoading}
              paginationMode="server"
              rowCount={data?.total || 0}
              paginationModel={{ page, pageSize }}
              onPaginationModelChange={(model) => { setPage(model.page); setPageSize(model.pageSize); }}
              pageSizeOptions={[10, 25, 50, 100]}
              autoHeight
              disableSelectionOnClick
              localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
              sx={{
                border: 'none',
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'action.hover',
                  cursor: 'pointer',
                },
              }}
              onRowClick={(params) => navigate(`/crm/clienti/${params.id}`)}
            />
          )}
        </CardContent>
      </Card>

      {/* Menu Azioni */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleView}>
          <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>
          Visualizza
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          Modifica
        </MenuItem>
        <MenuItem onClick={handleViewContracts}>
          <ListItemIcon><DescriptionIcon fontSize="small" /></ListItemIcon>
          Contratti
        </MenuItem>
        <MenuItem onClick={handleViewInvoices}>
          <ListItemIcon><ReceiptIcon fontSize="small" /></ListItemIcon>
          Fatture
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          Elimina
        </MenuItem>
      </Menu>

      {/* Dialog Conferma Eliminazione */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} fullScreen={isMobile}>
        <DialogTitle>Conferma Eliminazione</DialogTitle>
        <DialogContent>
          <Typography>
            Sei sicuro di voler eliminare il cliente{' '}
            <strong>
              {selectedCustomer?.business_name || 
               `${selectedCustomer?.first_name} ${selectedCustomer?.last_name}`}
            </strong>?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Questa azione non può essere annullata. Tutti i contratti e le fatture
            associate verranno mantenuti ma senza riferimento al cliente.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Annulla
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={deleteMutation.isLoading}
          >
            Elimina
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerList;
